/**
 * PlatformEditor
 *
 * Dynamic platform configuration editor.
 * Renders the appropriate editor based on platform type.
 *
 * @module analytics-settings/components/platform-editors
 */

import React from 'react';
import type { AnalyticsIntegration, AnalyticsPlatform, PlatformConfig } from '../../types/analytics.types';
import { AmplitudeEditor } from './AmplitudeEditor';
import { GA4Editor } from './GA4Editor';
import { GTMEditor } from './GTMEditor';
import { MixpanelEditor } from './MixpanelEditor';
import { PostHogEditor } from './PostHogEditor';
import { FacebookPixelEditor } from './FacebookPixelEditor';
import { TikTokPixelEditor } from './TikTokPixelEditor';
import { CustomScriptEditor } from './CustomScriptEditor';
import { SegmentlyInternalAnalyticsEditor } from './SegmentlyInternalAnalyticsEditor';
// Server-side platform editors
import { FacebookCAPIEditor } from './FacebookCAPIEditor';
import { GA4MeasurementProtocolEditor } from './GA4MeasurementProtocolEditor';
import { AmplitudeServerEditor } from './AmplitudeServerEditor';
import { MixpanelServerEditor } from './MixpanelServerEditor';
import { PostHogServerEditor } from './PostHogServerEditor';
import { TikTokEventsAPIEditor } from './TikTokEventsAPIEditor';

export interface PlatformEditorProps {
  platform: AnalyticsPlatform;
  config: PlatformConfig;
  name?: string;
  onChange: (config: PlatformConfig, name?: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
  showSaveButton?: boolean;
  onSave?: (config: PlatformConfig, name?: string) => void;
  /** Project ID - required for server-side platforms (Facebook CAPI test events) */
  projectId?: string;
  /** Sibling integrations in the same project (FacebookPixelEditor D5 conflict check; other editors ignore it). */
  siblingIntegrations?: AnalyticsIntegration[];
}

/**
 * PlatformEditor - Renders platform-specific configuration editor
 */
export const PlatformEditor: React.FC<PlatformEditorProps> = ({
  platform,
  config,
  name,
  onChange,
  disabled = false,
  readOnly = false,
  showSaveButton = false,
  onSave,
  projectId,
  siblingIntegrations,
}) => {
  const commonProps = {
    config,
    name,
    onChange,
    disabled,
    readOnly,
    showSaveButton,
    onSave,
    siblingIntegrations,
  };

  // Props for server-side platforms that need projectId
  const serverSideProps = {
    ...commonProps,
    projectId,
  };

  switch (platform) {
    case 'segmently_internal':
      return <SegmentlyInternalAnalyticsEditor {...commonProps} />;
    case 'amplitude':
      return <AmplitudeEditor {...commonProps} />;
    case 'ga4':
      return <GA4Editor {...commonProps} />;
    case 'gtm':
      return <GTMEditor {...commonProps} />;
    case 'mixpanel':
      return <MixpanelEditor {...commonProps} />;
    case 'posthog':
      return <PostHogEditor {...commonProps} />;
    case 'facebook_pixel':
      return <FacebookPixelEditor {...commonProps} />;
    case 'tiktok_pixel':
      return <TikTokPixelEditor {...commonProps} />;
    case 'custom_script':
      return <CustomScriptEditor {...commonProps} />;
    // Server-side platforms
    case 'facebook_capi':
      return <FacebookCAPIEditor {...serverSideProps} />;
    case 'ga4_measurement_protocol':
      return <GA4MeasurementProtocolEditor {...commonProps} />;
    case 'amplitude_server':
      return <AmplitudeServerEditor {...commonProps} />;
    case 'mixpanel_server':
      return <MixpanelServerEditor {...commonProps} />;
    case 'posthog_server':
      return <PostHogServerEditor {...commonProps} />;
    case 'tiktok_events_api':
      return <TikTokEventsAPIEditor {...serverSideProps} />;
    default:
      return null;
  }
};

// Re-export individual editors (client-side)
export { AmplitudeEditor } from './AmplitudeEditor';
export { GA4Editor } from './GA4Editor';
export { GTMEditor } from './GTMEditor';
export { MixpanelEditor } from './MixpanelEditor';
export { PostHogEditor } from './PostHogEditor';
export { FacebookPixelEditor } from './FacebookPixelEditor';
export { TikTokPixelEditor } from './TikTokPixelEditor';
export { CustomScriptEditor } from './CustomScriptEditor';
export { SegmentlyInternalAnalyticsEditor } from './SegmentlyInternalAnalyticsEditor';
// Re-export server-side editors
export { FacebookCAPIEditor } from './FacebookCAPIEditor';
export { GA4MeasurementProtocolEditor } from './GA4MeasurementProtocolEditor';
export { AmplitudeServerEditor } from './AmplitudeServerEditor';
export { MixpanelServerEditor } from './MixpanelServerEditor';
export { PostHogServerEditor } from './PostHogServerEditor';
export { TikTokEventsAPIEditor } from './TikTokEventsAPIEditor';
